import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileText, Lock, History, AlertTriangle, Save, Pencil, Check, Send, ClipboardCheck,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useSession } from '../lib/SessionContext';
import { t, formatDate, formatTime, formatDateTime } from '../i18n';
import {
  Card, Button, Select, Field, Badge, Alert, Spinner, EmptyState, Modal, Textarea,
} from '../components/ui';
import FormAnswer from '../components/FormAnswer';
import { scoreAnswers } from '../lib/forms';

/**
 * Historia Clínica — one entry per session, for one patient at a time.
 *
 * Readable only by the professional who wrote it. Another professional treating
 * the same family cannot see it, and neither can admin; that is enforced by RLS
 * on clinical_notes, not by this screen.
 *
 * Notes stay editable indefinitely, and every previous version is preserved —
 * a health record that can be silently rewritten is a liability.
 *
 * Assessment forms sit with the notes (migration 0065): sent to the patient or
 * filled in during a session, and read only by the professional who sent them.
 */

const NoteEditor = ({ appointment, note, onSaved, onClose }) => {
  const [content, setContent] = useState(note?.content ?? '');
  const [revisions, setRevisions] = useState([]);
  const [showRevisions, setShowRevisions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!note?.id) { setRevisions([]); return; }
    supabase
      .from('clinical_note_revisions')
      .select('id, content, created_at')
      .eq('clinical_note_id', note.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => setRevisions(data ?? []));
  }, [note?.id]);

  const save = async () => {
    if (!content.trim()) { setError(t('clinicalHistory.emptyNote')); return; }
    setSaving(true);
    setError(null);
    const { error: saveError } = await supabase.rpc('save_clinical_note', {
      p_appointment_id: appointment.id,
      p_content: content,
    });
    setSaving(false);
    if (saveError) { setError(saveError.message); return; }
    onSaved();
  };

  return (
    <Modal
      open
      wide
      title={`${t('clinicalHistory.entry')} · ${formatDate(appointment.starts_at)}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={save} loading={saving}><Save size={16} />{t('common.save')}</Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}

        <div className="flex flex-wrap gap-3 rounded-lg bg-slate-50 p-3 text-sm">
          <span className="text-slate-600">
            {formatDate(appointment.starts_at)} · {formatTime(appointment.starts_at)}
          </span>
          {appointment.patientAge != null && (
            <Badge tone="info">
              {t('clinicalHistory.patientAgeAtSession')}: {t('clinicalHistory.yearsOld', { age: appointment.patientAge })}
            </Badge>
          )}
        </div>

        <Field label={t('clinicalHistory.sessionNotes')}>
          <Textarea
            rows={12}
            value={content}
            onChange={(event) => setContent(event.target.value)}
            placeholder={t('clinicalHistory.placeholder')}
          />
        </Field>

        {note && (
          <p className="text-xs text-slate-500">
            {t('clinicalHistory.lastEdited', { date: formatDateTime(note.updated_at) })}
          </p>
        )}

        {revisions.length > 0 && (
          <div className="rounded-lg border border-slate-200">
            <button
              onClick={() => setShowRevisions((open) => !open)}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <History size={14} />
              {t('clinicalHistory.revisionCount', { count: revisions.length })}
            </button>
            {showRevisions && (
              <ul className="divide-y divide-slate-100 border-t border-slate-200">
                {revisions.map((revision) => (
                  <li key={revision.id} className="px-3 py-2">
                    <p className="text-xs text-slate-500">{formatDateTime(revision.created_at)}</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{revision.content}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

/** One answer as the professional reads it: a scale's value and its label. */
const answerText = (question, answer) => {
  if (question.kind === 'choice') {
    // The answer is the option chosen, by position.
    const option = (question.options ?? [])[answer];
    if (!option) return t('forms.noAnswer');
    return `${option.score} · ${option.label}`;
  }
  if (question.kind === 'scale') {
    if (typeof answer !== 'number') return t('forms.noAnswer');
    const label = question.labels?.[answer - Number(question.min)];
    return label ? `${answer} · ${label}` : String(answer);
  }
  if (question.kind === 'yes_no') {
    if (answer === true) return t('common.yes');
    if (answer === false) return t('common.no');
    return t('forms.noAnswer');
  }
  return answer || t('forms.noAnswer');
};

const scoreText = (total, band) => `${t('forms.score', { total })}${band ? ` · ${band}` : ''}`;

/** "Ánimo 12 · Alto · Anhedonia 4": what each subscale came to (0068). */
const scalesText = (scores) => (scores ?? [])
  .map((row) => (row.band
    ? t('forms.scaleScoreBand', { label: row.label, total: row.total, band: row.band })
    : t('forms.scaleScore', { label: row.label, total: row.total })))
  .join(' · ');


const AnswersModal = ({ result, onClose }) => (
  <Modal
    open
    wide
    title={`${result.title} · ${formatDate(result.answered_at)}`}
    onClose={onClose}
    footer={<Button variant="secondary" onClick={onClose}>{t('common.close')}</Button>}
  >
    <div className="space-y-4">
      <p className="text-sm font-semibold text-slate-900">{scoreText(result.total_score, result.band_label)}</p>
      {scalesText(result.scale_scores) && (
        <p className="text-sm text-slate-600">
          <span className="font-medium">{t('forms.subscores')}:</span> {scalesText(result.scale_scores)}
        </p>
      )}
      <ol className="space-y-3">
        {(result.form_snapshot?.questions ?? []).map((question, index) => (
          // Answers line up with questions by position, which never changes
          // after sending (the send keeps its own copy of the form).
          // eslint-disable-next-line react/no-array-index-key
          <li key={index} className="text-sm">
            <p className="text-slate-600">{index + 1}. {question.prompt}</p>
            <p className="mt-0.5 whitespace-pre-wrap font-medium text-slate-900">
              {answerText(question, result.answers?.[index])}
            </p>
          </li>
        ))}
      </ol>
    </div>
  </Modal>
);

const InSessionForm = ({ form, patient, onClose, onSaved }) => {
  const [answers, setAnswers] = useState(() => form.questions.map(() => null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const live = scoreAnswers(form.questions, form.bands, answers, form.scales);

  const save = async () => {
    if (live.missing) { setError(t('forms.missingAnswer', { n: live.missing })); return; }
    setBusy(true);
    setError(null);
    const { data, error: saveError } = await supabase.rpc('record_form_in_session', {
      p_form_id: form.id, p_dependent_id: patient.id, p_answers: answers,
    });
    setBusy(false);
    if (saveError) { setError(saveError.message); return; }
    onSaved(t('forms.recorded', { title: form.title, score: scoreText(data.total_score, data.band_label) }));
  };

  return (
    <Modal
      open
      wide
      title={t('forms.recordTitle', { title: form.title, name: `${patient.first_name} ${patient.last_name}` })}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={save} loading={busy}><Save size={16} />{t('forms.record')}</Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}
        <FormAnswer questions={form.questions} answers={answers} onChange={setAnswers} idPrefix="session" />
        <p className="text-sm font-semibold text-slate-800">{scoreText(live.total, live.band)}</p>
        {scalesText(live.scales) && <p className="text-sm text-slate-600">{scalesText(live.scales)}</p>}
      </div>
    </Modal>
  );
};

/**
 * The assessment forms of one patient, as this professional sent or filled
 * them in (0065). A form marked for sessions, PHQ-9 among them, is filled in
 * here and never sent.
 */
const FormsPanel = ({ patient }) => {
  const [forms, setForms] = useState([]);
  const [results, setResults] = useState(null);
  const [formId, setFormId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [filling, setFilling] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    const [formResult, resultResult] = await Promise.all([
      supabase.rpc('my_forms'),
      supabase.rpc('patient_form_results', { p_dependent_id: patient.id }),
    ]);
    const loadError = formResult.error ?? resultResult.error;
    if (loadError) setError(loadError.message);
    setForms((formResult.data ?? []).filter((form) => form.is_active && form.ready));
    setResults(resultResult.data ?? []);
  }, [patient.id]);

  useEffect(() => {
    setNotice(null);
    setError(null);
    setFormId('');
    load();
  }, [load]);

  const chosen = forms.find((form) => form.id === formId);

  const send = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    const { error: sendError } = await supabase.rpc('send_form', { p_form_id: formId, p_dependent_id: patient.id });
    setBusy(false);
    if (sendError) { setError(sendError.message); return; }
    setNotice(t('forms.sent', { title: chosen.title }));
    setFormId('');
    load();
  };

  const withdraw = async (row) => {
    setError(null);
    const { error: withdrawError } = await supabase.rpc('withdraw_form_send', { p_send_id: row.id });
    if (withdrawError) { setError(withdrawError.message); return; }
    load();
  };

  const whoFilled = (row) => {
    if (row.filled_by === 'professional') return t('forms.filledInSession');
    return t(row.status === 'answered' ? 'forms.filledByPatient' : 'forms.sentToPatient');
  };

  return (
    <Card title={t('forms.historyTitle')} subtitle={t('forms.historyHint')}>
      <div className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}
        {notice && <Alert tone="success">{notice}</Alert>}

        {forms.length === 0 ? (
          <p className="text-sm text-slate-500">{t('forms.noneReady')}</p>
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-0 flex-1 sm:max-w-sm">
              <Field label={t('forms.pickForm')}>
                <Select value={formId} onChange={(event) => setFormId(event.target.value)}>
                  <option value="">—</option>
                  {forms.map((form) => (
                    <option key={form.id} value={form.id}>
                      {form.title}{form.in_session_only ? ` · ${t('forms.inSessionOnly')}` : ''}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Button variant="secondary" onClick={send} loading={busy} disabled={!chosen || chosen.in_session_only}>
              <Send size={16} />{t('forms.send')}
            </Button>
            <Button variant="secondary" onClick={() => setFilling(chosen)} disabled={!chosen}>
              <ClipboardCheck size={16} />{t('forms.fillInSession')}
            </Button>
          </div>
        )}

        {results === null ? <Spinner /> : results.length === 0 ? (
          <p className="text-sm text-slate-500">{t('forms.noResults')}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {results.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm">
                <div className="min-w-0">
                  <p className="font-medium text-slate-900">{row.title}</p>
                  <p className="text-xs text-slate-500">
                    {formatDate(row.answered_at ?? row.withdrawn_at ?? row.sent_at)} · {whoFilled(row)}
                  </p>
                  {scalesText(row.scale_scores) && (
                    <p className="text-xs text-slate-500">{scalesText(row.scale_scores)}</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {row.status === 'answered' ? (
                    <>
                      <Badge tone="info">{scoreText(row.total_score, row.band_label)}</Badge>
                      <Button variant="ghost" onClick={() => setViewing(row)}>{t('forms.viewAnswers')}</Button>
                    </>
                  ) : (
                    <Badge tone={row.status === 'pending' ? 'warning' : 'neutral'}>{t(`forms.status.${row.status}`)}</Badge>
                  )}
                  {row.status === 'pending' && (
                    <Button variant="ghost" onClick={() => withdraw(row)}>{t('forms.withdraw')}</Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {filling && (
        <InSessionForm
          form={filling}
          patient={patient}
          onClose={() => setFilling(null)}
          onSaved={(message) => { setFilling(null); setNotice(message); setFormId(''); load(); }}
        />
      )}
      {viewing && <AnswersModal result={viewing} onClose={() => setViewing(null)} />}
    </Card>
  );
};

const ClinicalHistoryView = () => {
  const { therapistId } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();

  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState(searchParams.get('paciente') ?? '');
  const [sessions, setSessions] = useState([]);
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      // Everyone this professional has treated, discharged ones included: their
      // history stays theirs to read and correct (migration 0040).
      const { data } = await supabase
        .from('treatments')
        .select('dependents ( id, first_name, last_name, date_of_birth )')
        .eq('therapist_id', therapistId)
        .or('status.in.(approved,revoked),approved_at.not.is.null');
      const rows = (data ?? [])
        .map((row) => row.dependents)
        .filter(Boolean)
        .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, 'es'));
      setPatients(rows);
      // Functional form so the effect need not depend on patientId: this only
      // seeds an initial selection and must not clobber the user's choice.
      if (rows.length) setPatientId((current) => current || rows[0].id);
      setLoading(false);
    };
    load();
  }, [therapistId]);

  const patient = patients.find((row) => row.id === patientId);

  const loadSessions = useCallback(async () => {
    if (!patientId) { setSessions([]); return; }
    setLoadingSessions(true);
    setError(null);

    const [sessionResult, noteResult] = await Promise.all([
      supabase
        .from('appointments')
        .select('id, starts_at, status, location_id, locations(name)')
        .eq('dependent_id', patientId)
        .eq('therapist_id', therapistId)
        .in('status', ['completed', 'in_session'])
        .order('starts_at', { ascending: false }),
      supabase
        .from('clinical_notes')
        .select('id, appointment_id, content, patient_age_years, created_at, updated_at')
        .eq('dependent_id', patientId)
        .eq('therapist_id', therapistId),
    ]);

    if (sessionResult.error) setError(sessionResult.error.message);
    setSessions(sessionResult.data ?? []);
    setNotes(Object.fromEntries((noteResult.data ?? []).map((note) => [note.appointment_id, note])));
    setLoadingSessions(false);
  }, [patientId, therapistId]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const ageAtSession = useCallback((startsAt) => {
    if (!patient?.date_of_birth) return null;
    const birth = new Date(patient.date_of_birth);
    const on = new Date(startsAt);
    let age = on.getFullYear() - birth.getFullYear();
    const months = on.getMonth() - birth.getMonth();
    if (months < 0 || (months === 0 && on.getDate() < birth.getDate())) age -= 1;
    return age;
  }, [patient]);

  const pendingCount = useMemo(
    () => sessions.filter((session) => session.status === 'completed' && !notes[session.id]).length,
    [sessions, notes]
  );

  if (loading) return <Spinner />;

  if (patients.length === 0) {
    return (
      <Card title={t('clinicalHistory.title')}>
        <EmptyState icon={FileText} title={t('booking.noPatients')} />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t('clinicalHistory.title')}</h1>
        <p className="flex items-center gap-1.5 text-sm text-slate-500">
          <Lock size={13} />{t('clinicalHistory.privateNotice')}
        </p>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <Card>
        <div className="flex flex-wrap items-end gap-4">
          <Field label={t('appointments.patient')}>
            <Select
              value={patientId}
              onChange={(event) => {
                setPatientId(event.target.value);
                setSearchParams({ paciente: event.target.value });
              }}
              className="min-w-56"
            >
              {patients.map((row) => (
                <option key={row.id} value={row.id}>{row.first_name} {row.last_name}</option>
              ))}
            </Select>
          </Field>
          {patient && (
            <div className="text-sm text-slate-600">
              <p>{t('patients.dateOfBirth')}: {formatDate(patient.date_of_birth)}</p>
              <p className="text-xs text-slate-500">
                {t('clinicalHistory.sessionCount', { count: sessions.length })}
              </p>
            </div>
          )}
        </div>
      </Card>

      {patient && <FormsPanel patient={patient} />}

      {pendingCount > 0 && (
        <Alert tone="warning" title={t('clinicalHistory.pendingTitle')}>
          {t('clinicalHistory.pendingBody', { count: pendingCount })}
        </Alert>
      )}

      {loadingSessions ? <Spinner /> : sessions.length === 0 ? (
        <Card><EmptyState icon={FileText} title={t('clinicalHistory.noSessions')} /></Card>
      ) : (
        <div className="space-y-3">
          {sessions.map((session) => {
            const note = notes[session.id];
            const age = note?.patient_age_years ?? ageAtSession(session.starts_at);
            return (
              <Card key={session.id}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-slate-900">
                        {formatDate(session.starts_at)}
                      </span>
                      <span className="text-sm text-slate-500">{formatTime(session.starts_at)}</span>
                      {age != null && (
                        <Badge tone="neutral">{t('clinicalHistory.yearsOld', { age })}</Badge>
                      )}
                      {session.locations?.name && (
                        <span className="text-xs text-slate-400">{session.locations.name}</span>
                      )}
                      {note ? (
                        <Badge tone="success"><Check size={12} />{t('clinicalHistory.written')}</Badge>
                      ) : (
                        <Badge tone="warning">
                          <AlertTriangle size={12} />{t('clinicalHistory.pendingNote')}
                        </Badge>
                      )}
                    </div>

                    {note ? (
                      <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700">{note.content}</p>
                    ) : (
                      <p className="mt-3 text-sm italic text-slate-400">
                        {t('clinicalHistory.noNoteYet')}
                      </p>
                    )}
                  </div>

                  <Button
                    variant={note ? 'secondary' : 'primary'}
                    onClick={() => setEditing({
                      appointment: { ...session, patientAge: age },
                      note,
                    })}
                  >
                    {note ? <><Pencil size={14} />{t('common.edit')}</>
                          : <><FileText size={14} />{t('clinicalHistory.newEntry')}</>}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {editing && (
        <NoteEditor
          appointment={editing.appointment}
          note={editing.note}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadSessions(); }}
        />
      )}
    </div>
  );
};

export default ClinicalHistoryView;
